/**
 * Which AI crawlers came, and to which site — the per-crawler breakdown of
 * `cf.ai_crawler.requests` summed over the window. A table, not a chart:
 * more than seven classes carry meaning here (dataviz form rule).
 */
import { SITE_LABEL, SITE_ORDER, compact } from '../../lib/analyticsShape.js';
import { EstimatedTag, VisibilityTag } from './StatTile.js';
import type { MetricVisibility } from '../../lib/api/analytics.js';

export interface CrawlerRow {
  key: string;
  total: number;
  bySite: Record<string, number>;
  estimated: boolean;
}

export interface CrawlerTableProps {
  rows: CrawlerRow[];
  sites: string[];
  from: string;
  to: string;
  stale?: boolean;
  /** The same table serves any per-day dimension breakdown (crawler agents, search queries). */
  title?: string;
  note?: string;
  keyLabel?: string;
  valueLabel?: string;
  emptyText?: string;
  footer?: string;
  /** Internal by default: per-agent and per-query detail is internal. */
  visibility?: MetricVisibility;
}

export function CrawlerTable({
  rows, sites, from, to, stale,
  title = 'AI crawlers by agent',
  note,
  keyLabel = 'Agent',
  valueLabel = 'Requests',
  emptyText = 'No crawler rows in this window',
  footer = 'Cloudflare · adaptive dataset, request-source eyeball · retained 8 days at the source, kept here for good',
  visibility = 'internal',
}: CrawlerTableProps) {
  const cols = SITE_ORDER.filter((s) => sites.includes(s));
  const estimated = rows.filter((r) => r.estimated).length;
  const noteText = note ?? `Requests from AI retrieval and training agents, ${from} → ${to}. Per-agent detail stays internal; the total is the public-safe figure.`;
  return (
    <section className={`bg-surface rounded-xl border border-grey-200 p-4 flex flex-col gap-3 ${stale ? 'opacity-60' : ''} transition-opacity`} aria-busy={stale}>
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-grey-900">{title}</h3>
          <p className="text-xs text-grey-500 mt-0.5">{noteText}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <VisibilityTag visibility={visibility} />
          <EstimatedTag count={estimated} />
        </div>
      </header>
      {rows.length === 0 ? (
        <p className="text-sm text-grey-400 py-6 text-center">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-grey-500">
                <th className="py-1 pr-3 font-medium">{keyLabel}</th>
                <th className="py-1 pr-3 font-medium text-right">{valueLabel}</th>
                {cols.map((s) => <th key={s} className="py-1 pr-3 font-medium text-right">{SITE_LABEL[s]}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-grey-100 text-grey-900">
                  <td className="py-1 pr-3 font-medium">{r.key}{r.estimated ? <span className="text-warning-600" title="includes estimated days"> ≈</span> : null}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{compact(r.total)}</td>
                  {cols.map((s) => (
                    <td key={s} className="py-1 pr-3 text-right tabular-nums text-grey-600">{r.bySite[s] != null ? compact(r.bySite[s]) : <span className="text-grey-300">—</span>}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <footer className="text-[11px] text-grey-400">{footer}</footer>
    </section>
  );
}
